<template>
    <div class="waffle-container">
        <svg
            :width="size"
            :height="size"
        >
            <rect
                v-for="(r, i) in rects"
                :key="i"
                :class="{
                    background: true,
                    highlight: i <= highlightIdx,
                }"
                v-bind="r"
                @mouseover="onMouseOver(r)"
                @mouseout="onMouseOut(r)"
            />
            <circle
                v-if="compareIdx !== -1"
                class="compare-value"
                :cx="rects[compareIdx].x + scale.bandwidth() / 2"
                :cy="rects[compareIdx].y + scale.bandwidth() / 2"
                :r="scale.bandwidth() / 2 * 0.5"
            />
        </svg>
        <div
            v-if="hoverRect"
            ref="tooltip"
            class="tooltip-content"
            :style="tooltipPos"
        >
            <slot
                name="tooltip-content"
                :value="hoverRect.value"
                :type="hoverRect.type"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';

const { scaleBand } = d3;

export default {
    props: {
        value: {
            type: Number,
            default: 0
        },
        compareValue: {
            type: Number,
            default: 0
        },
        // This chart must be square, use for both width and height
        size: {
            type: Number,
            default: 100
        }
    },
    data() {
        return {
            hoverRect: null
        };
    },
    computed: {
        scale() {
            return scaleBand()
                .padding(0)
                .domain(Array.from({ length: 10 }).fill(0).map((v, i) => i))
                .range([0, this.size]);
        },
        tooltipPos() {
            if (this.$screens.mobile && this.hoverRect.x > this.size / 2.5) {
                const top = this.hoverRect.y + this.scale.bandwidth();

                const right = -10;
                return { right: `${right}px`, top: `${top}px` };
            }
            const top = this.hoverRect.y - this.scale.bandwidth() / 2;
            const left = this.hoverRect.x + this.scale.bandwidth() + 5;
            return { left: `${left}px`, top: `${top}px` };
        },
        rects() {
            return Array.from({ length: 100 }).fill(0).map((v, i) => ({
                i,
                x: this.scale(i % 10),
                y: this.scale(Math.floor((99 - i) / 10)),
                width: this.scale.bandwidth(),
                height: this.scale.bandwidth()
            }));
        },
        highlightIdx() {
            if (this.value == null) {
                return -1;
            }
            return _.round(this.value * 100) - 1;
        },
        compareIdx() {
            if (this.compareValue == null) {
                return -1;
            }
            return _.round(this.compareValue * 100) - 1;
        }
    },
    async updated() {
        await this.$nextTick();
        if (this.hoverRect) {
            this.fixTooltipPosition();
        }
    },
    methods: {
        onMouseOver(rect) {
            if (rect.i === this.compareIdx) {
                this.hoverRect = { ...rect, type: 'compare', value: this.compareValue };
            } else if (rect.i <= this.highlightIdx) {
                // eslint-disable-next-line no-param-reassign
                rect = this.rects[this.highlightIdx];
                this.hoverRect = { ...rect, type: 'value', value: this.value };
            }
            this.$emit('hover', this.hoverRect);
        },
        onMouseOut() {
            this.hoverRect = null;
            this.$emit('hover', null);
        },
        fixTooltipPosition() {
            const rect = this.$refs.tooltip.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                // Adjust tooltip to be on screen with 5px padding
                const adjustment = (rect.right - window.innerWidth) + 25;
                this.$refs.tooltip.style.transform = `translateX(-${adjustment}px)`;
            } else if (rect.left < 0) {
                const adjustment = -rect.left + 5;
                this.$refs.tooltip.style.transform = `translateX(${adjustment}px)`;
            }
        }
    }
};
</script>

<style src="./common.chart.css" scoped></style>
<style scoped lang="postcss">
.waffle-container {
    @apply relative;
}
rect {
    @apply fill-current;
    stroke-width: 1px;
    stroke: white;
}

rect:not(.highlight) {
    @apply text-neutral-4;
}

circle.compare-value {
    @apply text-neutral-1 fill-current pointer-events-none;
}

</style>
